// components/modals/CustomerDetailModal.js
import React, { useState, useMemo, useEffect } from 'react';
import { Button, Badge, Loading, ErrorMessage } from '../common';
import { AppointmentHistory } from '../customer/AppointmentHistory';
import { CustomerNotes } from '../customer/CustomerNotes';
import { AppointmentModal } from './AppointmentModal';
import { useAppointmentsPG } from '../../hooks/useAppointmentsPG';
import { useServicesPG } from '../../hooks/useServicesPG';
import { useTranslations } from '../../hooks/useTranslations';
import CustomerAppointmentStatusChart from '../charts/CustomerAppointmentStatusChart';

export function CustomerDetailModal({
  isOpen,
  onClose,
  customer,
  loading = false
}) {
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentsDataLoading, setAppointmentsDataLoading] = useState(true);

  const { getCustomerAppointments, handleAppointmentSubmit } = useAppointmentsPG();
  const { services } = useServicesPG();
  const t = useTranslations();

  // Get customer's appointments
  const [customerAppointments, setCustomerAppointments] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchAppointments = async () => {
      if (!customer?.id) {
        setCustomerAppointments([]);
        setAppointmentsDataLoading(false);
        return;
      }

      setAppointmentsDataLoading(true);
      try {
        const result = await getCustomerAppointments(customer.id);
        if (isMounted) setCustomerAppointments(result);
      } catch (err) {
        console.error('Failed to load customer appointments:', err);
        if (isMounted) setCustomerAppointments([]);
      } finally {
        if (isMounted) setAppointmentsDataLoading(false);
      }
    };

    if (isOpen && customer) {
      fetchAppointments();
    }

    return () => {
      isMounted = false;
    };
  }, [customer?.id, isOpen, getCustomerAppointments]);

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentModalOpen(true);
  };

  const handleAppointmentSubmitWithRefresh = async (appointmentData, mode = 'add') => {
    try {
      const result = await handleAppointmentSubmit(appointmentData, mode);

      if (result && result.success !== false) {
        try {
          const updatedAppointments = await getCustomerAppointments(customer.id);
          setCustomerAppointments(updatedAppointments);
        } catch (refreshError) {
          console.warn('Failed to refresh appointments after operation:', refreshError);
        }
      }

      return result;
    } catch (error) {
      console.error('Appointment operation failed:', error);
      throw error;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="customer-detail-modal" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Modal header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="customer-detail-modal">
                {t.customerDetails}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal content */}
          <div className="bg-gray-50 px-4 py-5 sm:p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {loading || !customer ? (
              <Loading text={t.loadingCustomerDetails} />
            ) : (
              <div className="space-y-6">
                {/* Customer Profile */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-start space-x-4">
                    {/* Avatar */}
                    <div className="w-16 h-16 mx-3 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 font-semibold text-xl">👤</span>
                    </div>

                    {/* Basic Info */}
                    <div className="flex-1">
                      <h2 className="text-xl font-bold ml-3 text-gray-900 mb-2">{customer.full_name}</h2>

                      {/* Contact Information */}
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-600">
                          <span className="w-5 ml-3">📞</span>
                          <span>{customer.phone}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="w-5 ml-3">✉️</span>
                          <span>{customer.email}</span>
                        </div>
                        {customer.birth_date && (
                          <div className="flex items-center text-gray-600">
                            <span className="w-5 ml-3">🎂</span>
                            <span>{new Date(customer.birth_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        <div className="flex items-center text-gray-600">
                          <span className="w-5 ml-3">📅</span>
                          <span>{t.customerSince} {new Date(customer.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Allergies & Notes */}
                  {(customer.allergies) && (
                    <div className="mt-4 space-y-3">
                      {customer.allergies && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <span className="text-orange-500 mt-0.5">⚠️</span>
                            <div>
                              <h3 className="font-medium text-red-800 text-sm mb-1">{t.allergiesAndSensitivities}</h3>
                              <p className="text-red-700 text-sm">{customer.allergies}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Customer Notes */}
                <CustomerNotes customerId={customer?.id} />

                {/* Customer Appointment Status Chart */}
                <CustomerAppointmentStatusChart
                  appointments={customerAppointments}
                  loading={appointmentsDataLoading}
                />

                {/* Appointment History */}
                <AppointmentHistory
                  appointments={customerAppointments}
                  loading={appointmentsDataLoading}
                  onAppointmentClick={handleAppointmentClick}
                />
              </div>
            )}
          </div>

          {/* Modal footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="w-full sm:w-auto"
            >
              {t.close || 'Close'}
            </Button>
          </div>
        </div>
      </div>

      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={() => {
          setIsAppointmentModalOpen(false);
          setSelectedAppointment(null);
        }}
        onSuccess={handleAppointmentSubmitWithRefresh}
        mode={selectedAppointment ? "edit" : "add"}
        appointment={selectedAppointment}
        services={services}
        prefilledCustomerId={selectedAppointment ? null : customer?.id}
      />
    </div>
  );
}
